import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { ChatAlertPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'

export const Route = createFileRoute('/_site/for/non-technical')({
  component: NonTechnicalPage,
  head: () => ({
    meta: [
      { title: 'Simple Website Monitoring — No Code Needed | Uptrack' },
      { name: 'description', content: 'Is your website up? Uptrack monitors it 24/7 with 30s checks and email alerts. No code needed — easy for anyone.' },
      { property: 'og:title', content: 'Simple Website Monitoring — No Code Needed | Uptrack' },
      { property: 'og:description', content: 'Enter your URL and done. Email alerts when your site goes down. No code needed. Free to start.' },
      { property: 'og:image', content: 'https://uptrack.app/og/for-non-technical.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/for/non-technical' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'Simple Monitoring', item: 'https://uptrack.app/for/non-technical' },
          ],
        }),
      },
    ],
  }),
})

function NonTechnicalPage() {
  return (
    <FeaturePage
      eyebrow='For · Non-technical owners'
      section='Non-technical owners'
      title='Is your website up? Find out in 30 seconds.'
      intro="No code. No technical knowledge. Just enter your website address and Uptrack watches it 24/7. You get an email the moment it goes down. That's it."
      featuresHeading='Website monitoring made simple'
      features={[
        { title: 'No code needed', desc: 'If you can type a website address, you can use Uptrack. No coding, no installation, no configuration.' },
        { title: 'Enter URL and done', desc: 'Sign up, paste your website URL, click save. Uptrack starts checking your site immediately.' },
        { title: 'Email alerts', desc: 'Your website goes down? You get an email right away. It comes back up? Another email. Simple as that.' },
        { title: 'Public status page', desc: 'Share a page that shows if your website is up or down. Great for your visitors and customers.' },
        { title: 'No technical knowledge', desc: 'Uptrack is designed to be understood by anyone. No jargon, no complexity, no learning curve.' },
        { title: 'Free to start', desc: 'Start free with 5 monitors, then choose Pro, Team, or Business based on how many sites and features you need.' },
      ]}
      why={{ heading: "You don't need to be technical", body: "You run a small business and your website is important. Maybe it's a restaurant with online ordering, a portfolio, or a local service. You don't have an IT team. You just need to know when your website goes down so you can call your hosting company and get it fixed. That's exactly what Uptrack does." }}
      cta={{ heading: 'Start monitoring in minutes', body: 'Just enter your website address. Start free, or choose a plan for more.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard/incidents">
          <ChatAlertPiece />
        </ProductFrame>
      }
    />
  )
}
